/**
 * AICopilot — Zenith Digital CRP Universal AI Agent Widget
 * Barcha ERP operatsiyalarini (Sotuv, Vitrina, Mahsulot, Kategoriya, Birlik, Mijoz, Kassa, Hisobotlar, Excel) to'liq bajaradi.
 */
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Drawer, Input, Button, Spin, message } from "antd";
import { useTranslation } from "react-i18next";
import { currentUiLang } from "../../utils/uiLanguages";
import {
  Send,
  X,
  Zap,
  Mic,
  MicOff,
  CheckCircle,
  Download,
  FileText,
  ShoppingBag,
  Box,
  Layers,
  UserPlus,
  DollarSign,
  Trash2,
} from "react-feather";
import { useHistory } from "react-router-dom";
import {
  AIChatMessage,
  streamGeminiChat,
  ActionDraft,
  ReportData,
  fetchERPContext,
  executeSaleAction,
  executeShowcaseAction,
  executeProductAction,
  executeCategoryAction,
  executeUnitAction,
  executeCounterpartyAction,
  executeMoneyAction,
} from "./aiService";
import { exportJSON2Excel } from "../../utils/exportToExcel";
import "./AICopilot.less";

const CHAT_STORAGE_KEY = "zenith_ai_chat_history";

/**
 * Salomlashish xabari tarjimadan olinadi — ilgari u shu yerda o'zbekcha
 * qotirib yozilgan edi va ruscha interfeysda ham o'zbekcha chiqardi.
 */
const initialMessages = (welcome: string): AIChatMessage[] => [
  {
    id: "welcome-1",
    sender: "ai",
    timestamp: new Date(),
    text: welcome,
  },
];

const loadSavedMessages = (welcome: string): AIChatMessage[] => {
  try {
    const saved = localStorage.getItem(CHAT_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((m: any) => ({
          ...m,
          timestamp: new Date(m.timestamp),
        }));
      }
    }
  } catch (e) {
    console.warn("Failed to load AI chat history:", e);
  }
  return initialMessages(welcome);
};

/** Tez buyruqlar: yorliq ham, matn ham tanlangan tilda bo'ladi */
const PROMPT_IDS = [
  "Sale",
  "Showcase",
  "Product",
  "Category",
  "Customer",
  "Report",
  "Expense",
] as const;

export default function AICopilot() {
  const history = useHistory();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<AIChatMessage[]>(() =>
    loadSavedMessages(String(t("aiWelcome")))
  );

  const suggestedPrompts = useMemo(
    () =>
      PROMPT_IDS.map((id) => ({
        label: String(t(`aiPrompt${id}Label`)),
        query: String(t(`aiPrompt${id}Query`)),
      })),
    [t]
  );
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [executingActionId, setExecutingActionId] = useState<string | null>(null);

  const feedEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  // Orqa fonda ERP keshini yuklab olish
  useEffect(() => {
    fetchERPContext(true);
  }, []);

  // Suhbat tarixini avtomatik saqlash
  useEffect(() => {
    try {
      const sanitized = messages.filter((m) => !m.isStreaming).slice(-50);
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(sanitized));
    } catch (e) {
      console.warn("Failed to save AI chat history:", e);
    }
  }, [messages]);

  const handleClearHistory = () => {
    localStorage.removeItem(CHAT_STORAGE_KEY);
    setMessages(initialMessages(String(t("aiWelcome"))));
    message.success(String(t("aiHistoryCleared")));
  };

  const scrollToBottom = () => {
    feedEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [messages, open]);

  // Speech Recognition sozlash (Universal)
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      // Ovoz tanish tili interfeys tiliga bog'lanadi
      recognition.lang =
        currentUiLang() === "ru" ? "ru-RU" : currentUiLang() === "en" ? "en-US" : "uz-UZ";

      recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join("");
        setInputValue(transcript);
      };

      recognition.onerror = (event: any) => {
        console.warn("Speech recognition error:", event.error);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
    }
  }, []);

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      message.warning(String(t("aiSpeechUnsupported")));
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
        message.info(String(t("aiPlaceholderRecording")));
      } catch (err) {
        console.warn("Recording start failed:", err);
      }
    }
  };

  const handleSend = async (queryText?: string) => {
    const textToSend = (queryText || inputValue).trim();
    if (!textToSend || loading) return;

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    }

    const userMessageId = `user-${Date.now()}`;
    const aiMessageId = `ai-${Date.now()}`;

    const userMessage: AIChatMessage = {
      id: userMessageId,
      sender: "user",
      text: textToSend,
      timestamp: new Date(),
    };

    const initialAiMessage: AIChatMessage = {
      id: aiMessageId,
      sender: "ai",
      text: "",
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMessage, initialAiMessage]);
    setInputValue("");
    setLoading(true);

    try {
      const { fullText, actionDraft, reportData } = await streamGeminiChat(
        textToSend,
        (accumulatedText) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId ? { ...msg, text: accumulatedText } : msg
            )
          );
        }
      );

      // Intellektual yo'naltiruvchi tugmalar
      const qLower = textToSend.toLowerCase();
      const actions: Array<{ label: string; actionType: "navigate" | "excel" | "copy"; payload: string }> = [];

      if (qLower.includes("kategoriya") || qLower.includes("category")) {
        actions.push({ label: String(t("products")), actionType: "navigate", payload: "/products" });
      }
      if (qLower.includes("vitrina") || qLower.includes("showcase")) {
        actions.push({ label: String(t("showcases")), actionType: "navigate", payload: "/showcases" });
      }
      if (qLower.includes("product") || qLower.includes("mahsulot") || qLower.includes("tovar")) {
        actions.push({ label: String(t("products")), actionType: "navigate", payload: "/products" });
      }
      if (qLower.includes("proxod") || qLower.includes("prixod") || qLower.includes("xarid") || qLower.includes("kirim")) {
        actions.push({ label: String(t("purchase")), actionType: "navigate", payload: "/purchases" });
      }
      if (qLower.includes("sotuv") || qLower.includes("hisobot") || qLower.includes("foyda")) {
        actions.push({ label: String(t("sale")), actionType: "navigate", payload: "/sales" });
      }
      if (qLower.includes("qarz") || qLower.includes("mijoz")) {
        actions.push({ label: String(t("counterparties")), actionType: "navigate", payload: "/counterparties" });
      }
      if (qLower.includes("ombor") || qLower.includes("qoldiq")) {
        actions.push({ label: String(t("residues")), actionType: "navigate", payload: "/residues" });
      }
      if (qLower.includes("kassa") || qLower.includes("moliya")) {
        actions.push({ label: String(t("money")), actionType: "navigate", payload: "/money" });
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === aiMessageId
            ? {
                ...msg,
                text: fullText,
                isStreaming: false,
                actionDraft,
                reportData,
                actions: actions.length > 0 ? actions : undefined,
              }
            : msg
        )
      );
    } catch (err: any) {
      console.error("AI send error:", err);
      // Haqiqiy xato matnini ko'rsatamiz — umumiy xabar bilan muammoni
      // aniqlash imkonsiz edi (42-band)
      const detail = String(err?.message || err || "").slice(0, 300);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === aiMessageId
            ? {
                ...msg,
                text: `⚠️ ${t("aiRequestFailed")}.\n\n${detail || t("unknownError")}\n\n${t("aiTryAgain")}`,
                isStreaming: false,
              }
            : msg
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteAction = async (msgId: string, draft: ActionDraft) => {
    setExecutingActionId(msgId);
    let success = false;
    let successMsg = "";
    let navigateTarget = "/";

    // Xabarlar tarjimadan olinadi: ilgari ular shu yerda o'zbekcha qotirib
    // yozilgan edi va til ruschaga qo'yilganda ham o'zbekcha chiqardi
    if (draft.type === "sale") {
      success = await executeSaleAction(draft);
      successMsg = `🎉 ${t("aiDoneSale", {
        customer: draft.customerName,
        total: draft.totalPriceUSD,
      })}`;
      navigateTarget = "/sales";
    } else if (draft.type === "showcase") {
      success = await executeShowcaseAction(draft);
      // "count" — i18next da ko'plik uchun band nom, shuning uchun "qty"
      successMsg = `🎉 ${t("aiDoneShowcase", {
        customer: draft.customerName,
        qty: draft.count,
      })}`;
      navigateTarget = "/showcases";
    } else if (draft.type === "product") {
      success = await executeProductAction(draft);
      successMsg = `🎉 ${t("aiDoneProduct", {
        name: draft.nameModel,
        article: draft.article,
      })}`;
      navigateTarget = "/products";
    } else if (draft.type === "category") {
      success = await executeCategoryAction(draft);
      successMsg = `🎉 ${t("aiDoneCategory", { name: draft.name })}`;
      navigateTarget = "/products";
    } else if (draft.type === "unit") {
      success = await executeUnitAction(draft);
      successMsg = `🎉 ${t("aiDoneUnit", { name: draft.name })}`;
      navigateTarget = "/products";
    } else if (draft.type === "counterparty") {
      success = await executeCounterpartyAction(draft);
      successMsg = `🎉 ${t("aiDoneCounterparty", { name: draft.name })}`;
      navigateTarget = "/counterparties";
    } else if (draft.type === "money") {
      success = await executeMoneyAction(draft);
      successMsg = `🎉 ${t("aiDoneMoney", { amount: draft.priceUSD })}`;
      navigateTarget = "/money";
    }

    setExecutingActionId(null);

    if (success) {
      setMessages((prev) => [
        ...prev,
        {
          id: `sys-${Date.now()}`,
          sender: "system",
          text: successMsg,
          timestamp: new Date(),
          actions: [{ label: "Bo‘limda ko‘rish ➔", actionType: "navigate", payload: navigateTarget }],
        },
      ]);
    }
  };

  const handleDownloadExcel = (report: ReportData) => {
    try {
      const excelData = report.rows.map((row) => {
        const obj: any = {};
        report.headers.forEach((h, i) => {
          obj[h] = row[i];
        });
        return obj;
      });

      exportJSON2Excel(excelData, report.title || "Hisobot", `${report.title || "Zenith_AI_Hisobot"}.xlsx`);
      message.success(t("msgExcelDownloaded"));
    } catch (e) {
      console.error("Excel download error:", e);
      message.error(t("msgExcelFailed"));
    }
  };

  const handleActionClick = (action: { actionType: string; payload: string }) => {
    if (action.actionType === "navigate") {
      history.push(action.payload);
      setOpen(false);
    }
  };

  return (
    <>
      {/* Floating AI Button */}
      <div className="zenith-ai-copilot-fab" onClick={() => setOpen(true)} title="Zenith AI Copilot">
        <div className="fab-icon-glow">
          <Zap size={18} />
          <span className="pulsing-dot" />
        </div>
        <span className="fab-label">AI Agent</span>
      </div>

      {/* Chat Drawer */}
      <Drawer
        visible={open}
        onClose={() => setOpen(false)}
        width={500}
        placement="right"
        className="zenith-ai-drawer"
        closable={false}
        title={
          <div className="ai-drawer-header-content">
            <div className="ai-header-info">
              <div className="ai-avatar-badge">
                <Zap size={20} />
              </div>
              <div>
                <h4 className="ai-title">Zenith Universal AI Agent</h4>
                <span className="ai-status">{t("aiSubtitle")}</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Button
                type="text"
                icon={<Trash2 size={16} color="rgba(255, 240, 230, 0.6)" />}
                onClick={handleClearHistory}
                title={String(t("aiClearHistory"))}
              />
              <Button
                type="text"
                icon={<X size={18} color="rgba(255, 240, 230, 0.7)" />}
                onClick={() => setOpen(false)}
                title={String(t("close"))}
              />
            </div>
          </div>
        }
      >
        {/* Message Feed */}
        <div className="ai-chat-feed">
          {messages.map((msg) => (
            <div key={msg.id} className={`ai-message-row ${msg.sender}`}>
              <div className={`message-avatar ${msg.sender}`}>
                {msg.sender === "ai" ? <Zap size={16} /> : msg.sender === "system" ? "✅" : "👤"}
              </div>

              {msg.isStreaming && !msg.text ? (
                /* Yagona jonli yuklanish pufakchasi */
                <div className="message-bubble" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Spin size="small" />
                  <span>AI bajarmoqda...</span>
                </div>
              ) : (
                <div className="message-bubble">
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {msg.text.split("\n").map((line, idx) => {
                      if (line.startsWith("* ")) {
                        return <p key={idx} style={{ margin: "2px 0 2px 8px" }}>• {line.substring(2)}</p>;
                      }
                      return <p key={idx}>{line}</p>;
                    })}
                  </div>

                  {/* Structured Report Table with Excel Download */}
                  {msg.reportData && (
                    <div className="ai-report-table-card">
                      <div className="report-table-header">
                        <span className="report-table-title">
                          <FileText size={15} color="#93C5FD" />
                          {msg.reportData.title || "Hisobot Jadvali"}
                        </span>
                        <button
                          className="report-excel-btn"
                          onClick={() => handleDownloadExcel(msg.reportData!)}
                        >
                          <Download size={13} />
                          Excel yuklab olish
                        </button>
                      </div>

                      <div className="report-table-wrapper">
                        <table>
                          <thead>
                            <tr>
                              {msg.reportData.headers.map((h, hIdx) => (
                                <th key={hIdx}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {msg.reportData.rows.map((row, rIdx) => (
                              <tr key={rIdx}>
                                {row.map((cell, cIdx) => (
                                  <td key={cIdx}>{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Universal Action Confirmation Cards */}
                  {msg.actionDraft && (
                    <div className={`ai-action-card ${msg.actionDraft.type}`}>
                      {/* Sale Card */}
                      {msg.actionDraft.type === "sale" && (
                        <>
                          <div className="action-card-header">
                            <span className="action-card-title">
                              <ShoppingBag size={15} color="#34D399" />
                              Sotuv Qoralamasi
                            </span>
                            <span className="action-badge" style={{ background: "#10B981" }}>
                              ${msg.actionDraft.totalPriceUSD}
                            </span>
                          </div>
                          <div className="action-grid">
                            <div className="action-field">
                              <span className="field-label">Mijoz</span>
                              <div className="field-val">{msg.actionDraft.customerName}</div>
                            </div>
                            <div className="action-field">
                              <span className="field-label">Tovar</span>
                              <div className="field-val">{msg.actionDraft.productName}</div>
                            </div>
                            <div className="action-field">
                              <span className="field-label">Miqdori x Narxi</span>
                              <div className="field-val">{msg.actionDraft.amount} dona x ${msg.actionDraft.priceUSD}</div>
                            </div>
                            <div className="action-field">
                              <span className="field-label">{t("aiPayment")}</span>
                              <div className="field-val" style={{ color: "#34D399" }}>
                                ${msg.actionDraft.cashUSD} / <span style={{ color: "#F87171" }}>${msg.actionDraft.debtUSD}</span>
                              </div>
                            </div>
                          </div>
                          <button
                            className="action-submit-btn"
                            style={{ background: "linear-gradient(135deg, #10B981, #059669)" }}
                            onClick={() => handleExecuteAction(msg.id, msg.actionDraft!)}
                            disabled={executingActionId === msg.id}
                          >
                            {executingActionId === msg.id ? <Spin size="small" /> : <CheckCircle size={16} />}
                            Sotuvni Bazaga Yozish ➔
                          </button>
                        </>
                      )}

                      {/* Showcase Card */}
                      {msg.actionDraft.type === "showcase" && (
                        <>
                          <div className="action-card-header">
                            <span className="action-card-title">
                              <Box size={15} color="#A78BFA" />
                              Vitrina Qoralamasi
                            </span>
                            <span className="action-badge" style={{ background: "#8B5CF6" }}>
                              {msg.actionDraft.count} dona
                            </span>
                          </div>
                          <div className="action-grid">
                            <div className="action-field">
                              <span className="field-label">{t("aiClientShop")}</span>
                              <div className="field-val">{msg.actionDraft.customerName}</div>
                            </div>
                            <div className="action-field">
                              <span className="field-label">Mahsulot</span>
                              <div className="field-val">{msg.actionDraft.productName}</div>
                            </div>
                          </div>
                          <button
                            className="action-submit-btn"
                            style={{ background: "linear-gradient(135deg, #8B5CF6, #7C3AED)" }}
                            onClick={() => handleExecuteAction(msg.id, msg.actionDraft!)}
                            disabled={executingActionId === msg.id}
                          >
                            {executingActionId === msg.id ? <Spin size="small" /> : <CheckCircle size={16} />}
                            Vitrinaga Qo‘shish ➔
                          </button>
                        </>
                      )}

                      {/* Product Card */}
                      {msg.actionDraft.type === "product" && (
                        <>
                          <div className="action-card-header">
                            <span className="action-card-title">
                              <Box size={15} color="#FBBF24" />
                              Yangi Mahsulot Qoralamasi
                            </span>
                            <span className="action-badge" style={{ background: "#F59E0B" }}>
                              ${msg.actionDraft.priceUSD}
                            </span>
                          </div>
                          <div className="action-grid">
                            <div className="action-field">
                              <span className="field-label">Artikul</span>
                              <div className="field-val">{msg.actionDraft.article}</div>
                            </div>
                            <div className="action-field">
                              <span className="field-label">Nomi</span>
                              <div className="field-val">{msg.actionDraft.nameModel}</div>
                            </div>
                          </div>
                          <button
                            className="action-submit-btn"
                            style={{ background: "linear-gradient(135deg, #F59E0B, #D97706)" }}
                            onClick={() => handleExecuteAction(msg.id, msg.actionDraft!)}
                            disabled={executingActionId === msg.id}
                          >
                            {executingActionId === msg.id ? <Spin size="small" /> : <CheckCircle size={16} />}
                            Mahsulotni Bazaga Qo‘shish ➔
                          </button>
                        </>
                      )}

                      {/* Category Card */}
                      {msg.actionDraft.type === "category" && (
                        <>
                          <div className="action-card-header">
                            <span className="action-card-title">
                              <Layers size={15} color="#38BDF8" />
                              Yangi Kategoriya Qoralamasi
                            </span>
                          </div>
                          <div className="action-grid">
                            <div className="action-field" style={{ gridColumn: "1 / -1" }}>
                              <span className="field-label">Kategoriya Nomi</span>
                              <div className="field-val">{msg.actionDraft.name}</div>
                            </div>
                          </div>
                          <button
                            className="action-submit-btn"
                            style={{ background: "linear-gradient(135deg, #0284C7, #0369A1)" }}
                            onClick={() => handleExecuteAction(msg.id, msg.actionDraft!)}
                            disabled={executingActionId === msg.id}
                          >
                            {executingActionId === msg.id ? <Spin size="small" /> : <CheckCircle size={16} />}
                            Kategoriyani Bazaga Qo‘shish ➔
                          </button>
                        </>
                      )}

                      {/* Unit Card */}
                      {msg.actionDraft.type === "unit" && (
                        <>
                          <div className="action-card-header">
                            <span className="action-card-title">
                              <Layers size={15} color="#A3E635" />
                              Yangi O‘lchov Birligi
                            </span>
                          </div>
                          <div className="action-grid">
                            <div className="action-field" style={{ gridColumn: "1 / -1" }}>
                              <span className="field-label">Birlik Nomi</span>
                              <div className="field-val">{msg.actionDraft.name}</div>
                            </div>
                          </div>
                          <button
                            className="action-submit-btn"
                            style={{ background: "linear-gradient(135deg, #65A30D, #4D7C0F)" }}
                            onClick={() => handleExecuteAction(msg.id, msg.actionDraft!)}
                            disabled={executingActionId === msg.id}
                          >
                            {executingActionId === msg.id ? <Spin size="small" /> : <CheckCircle size={16} />}
                            Birlikni Bazaga Qo‘shish ➔
                          </button>
                        </>
                      )}

                      {/* Counterparty Card */}
                      {msg.actionDraft.type === "counterparty" && (
                        <>
                          <div className="action-card-header">
                            <span className="action-card-title">
                              <UserPlus size={15} color="#60A5FA" />
                              Yangi Mijoz Qoralamasi
                            </span>
                          </div>
                          <div className="action-grid">
                            <div className="action-field">
                              <span className="field-label">F.I.O / Nomi</span>
                              <div className="field-val">{msg.actionDraft.name}</div>
                            </div>
                            <div className="action-field">
                              <span className="field-label">Telefon</span>
                              <div className="field-val">{msg.actionDraft.phone || "—"}</div>
                            </div>
                          </div>
                          <button
                            className="action-submit-btn"
                            style={{ background: "linear-gradient(135deg, #3B82F6, #2563EB)" }}
                            onClick={() => handleExecuteAction(msg.id, msg.actionDraft!)}
                            disabled={executingActionId === msg.id}
                          >
                            {executingActionId === msg.id ? <Spin size="small" /> : <CheckCircle size={16} />}
                            Mijozni Ro‘yxatga Olish ➔
                          </button>
                        </>
                      )}

                      {/* Money Card */}
                      {msg.actionDraft.type === "money" && (
                        <>
                          <div className="action-card-header">
                            <span className="action-card-title">
                              <DollarSign size={15} color="#F472B6" />
                              Kassa Operatsiyasi
                            </span>
                            <span className="action-badge" style={{ background: "#EC4899" }}>
                              ${msg.actionDraft.priceUSD}
                            </span>
                          </div>
                          <div className="action-grid">
                            <div className="action-field">
                              <span className="field-label">Turi</span>
                              <div className="field-val" style={{ textTransform: "capitalize" }}>
                                {msg.actionDraft.transactionType === "expense" ? "Xarajat (Chiqim)" : "Kirim"}
                              </div>
                            </div>
                            <div className="action-field">
                              <span className="field-label">Izoh</span>
                              <div className="field-val">{msg.actionDraft.comment || "—"}</div>
                            </div>
                          </div>
                          <button
                            className="action-submit-btn"
                            style={{ background: "linear-gradient(135deg, #EC4899, #DB2777)" }}
                            onClick={() => handleExecuteAction(msg.id, msg.actionDraft!)}
                            disabled={executingActionId === msg.id}
                          >
                            {executingActionId === msg.id ? <Spin size="small" /> : <CheckCircle size={16} />}
                            Kassaga Kiritish ➔
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  {msg.actions && (
                    <div className="ai-actions-row">
                      {msg.actions.map((act, aIdx) => (
                        <button
                          key={aIdx}
                          className="ai-action-btn"
                          onClick={() => handleActionClick(act)}
                        >
                          {act.label} →
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          <div ref={feedEndRef} />
        </div>

        {/* Quick Suggested Prompts */}
        <div className="ai-quick-prompts">
          {suggestedPrompts.map((item, idx) => (
            <div
              key={idx}
              className="quick-chip"
              onClick={() => handleSend(item.query)}
            >
              {item.label}
            </div>
          ))}
        </div>

        {/* Input Bar */}
        <div className="ai-input-bar">
          <Input
            className="ai-text-input"
            placeholder={String(isRecording ? t("aiPlaceholderRecording") : t("aiPlaceholder"))}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPressEnter={() => handleSend()}
            disabled={loading}
          />

          {/* Microphone Button */}
          <button
            className={`ai-mic-btn ${isRecording ? "recording" : ""}`}
            onClick={toggleRecording}
            title={String(isRecording ? t("aiMicStop") : t("aiMicStart"))}
          >
            {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
          </button>

          {/* Send Button */}
          <button
            className="ai-send-btn"
            onClick={() => handleSend()}
            disabled={loading || !inputValue.trim()}
          >
            <Send size={16} />
          </button>
        </div>
      </Drawer>
    </>
  );
}
